import React, { useEffect, useRef, useState } from "react";
import LoadingSpinner from "../../Components/LoadingSpinner/LoadingSpinner";
import { Constants } from "../../Utils/Constants";
import "./style.css";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import SeoHead from "../../Components/SeoHead/SeoHead";

const imagesList = require.context('../../../public/nuestros-trabajos', false, /\.(png|jpe?g|svg|webp)$/);

function OurWork() {
  const [images, setImages] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [slidePage, setSlidePage] = useState(1);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getImagesFromPublicFolder();
  }, []);

  const getImagesFromPublicFolder = () => {
    const imageUrls = imagesList.keys().map(element => element.replace("./", ""));
    setImages((oldImages) => [...oldImages, ...imageUrls]);
    setLoading(false);
    setFirstLoad(false);
  };

  const slideBack = () => {
    if (slideIndex > 0) {
      setSlideIndex(slideIndex - 1);
    } else if (!loading || (slidePage > 1 && loading)) {
      const pageToChange = slidePage > 1 ? slidePage - 1 : maxPage();
      setSlidePage(pageToChange);
      setSlideIndex(getItemsInPage(pageToChange) - 1);
    }
  };

  const slideForward = () => {
    if (slideIndex < 4 && slideIndex !== getItemsInPage(slidePage) - 1) {
      setSlideIndex(slideIndex + 1);
    } else if (!loading || (slidePage < maxPage() && loading)) {
      const pageToChange = slidePage < maxPage() ? slidePage + 1 : 1;
      setSlidePage(pageToChange);
      setSlideIndex(0);
    }
  };

  const maxPage = () => {
    return Math.ceil(images.length / Constants.IMAGES_PER_SLIDE_PAGE);
  };

  const arrayMinIndex = () => {
    return (slidePage - 1) * Constants.IMAGES_PER_SLIDE_PAGE;
  };

  const arrayMaxIndex = () => {
    return (slidePage - 1) * Constants.IMAGES_PER_SLIDE_PAGE + Constants.IMAGES_PER_SLIDE_PAGE;
  };

  const currentImageIndex = () => {
    return slideIndex + (Constants.IMAGES_PER_SLIDE_PAGE * slidePage - Constants.IMAGES_PER_SLIDE_PAGE);
  };

  const getItemsInPage = (page) => {
    const maxItems = page * Constants.IMAGES_PER_SLIDE_PAGE;
    const itemsDiff = images.length - maxItems;

    return itemsDiff > 0 ? Constants.IMAGES_PER_SLIDE_PAGE : Constants.IMAGES_PER_SLIDE_PAGE - Math.abs(itemsDiff);
  };

  const imageRef = useRef(null);

  const getImagePath = (image) => {
    return process.env.PUBLIC_URL + '/' + Constants.NUESTROS_TRABAJOS_PATH + '/' + image;
  }

  return (
    <>
      <SeoHead
        title="Nuestros Trabajos"
        path="/nuestros-trabajos"
        description="Galería de trabajos terminados de Marmolería Colaiacovo: instalaciones de mármol, granito y cuarzo en Buenos Aires. Más de 60 años de experiencia."
      />
      <div className="container mx-auto px-4">
        <h1 className="py-8 text-center font-bold section-title">NUESTROS TRABAJOS</h1>
        <section aria-label="Galería de trabajos" className="mx-auto max-w-2xl mb-8">
          {firstLoad && images.length === 0 ? (
            <LoadingSpinner show={firstLoad} />
          ) : (
            <div className="shadow-sm relative">
              <div className="current-image-container flex items-center justify-center overflow-hidden">
                <TransitionGroup className="contents">
                  <CSSTransition nodeRef={imageRef} appear={true} timeout={{enter: 300, exit: 0}} classNames="img-animation" key={slideIndex}>
                    <img
                      ref={imageRef}
                      className="current-image object-fill"
                      src={images.length > 0 ? getImagePath(images[currentImageIndex()]) : ""}
                      alt={`Trabajo terminado de marmolería — imagen ${currentImageIndex() + 1} de ${images.length}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </CSSTransition>
                </TransitionGroup>
                <button
                  type="button"
                  className="h-inherit absolute left-0 inset-y-0 flex items-center px-3 cursor-pointer border-0"
                  onClick={slideBack}
                  aria-label="Imagen anterior"
                  style={{ background: 'rgba(0,0,0,0.35)', transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(0,0,0,0.6)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(0,0,0,0.35)'}
                >
                  <span style={{ color: '#fff', fontSize: '1.5rem', lineHeight: 1 }}>‹</span>
                </button>
                <button
                  type="button"
                  className="h-inherit absolute right-0 inset-y-0 flex items-center px-3 cursor-pointer border-0"
                  onClick={slideForward}
                  aria-label="Imagen siguiente"
                  style={{ background: 'rgba(0,0,0,0.35)', transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(0,0,0,0.6)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(0,0,0,0.35)'}
                >
                  <span style={{ color: '#fff', fontSize: '1.5rem', lineHeight: 1 }}>›</span>
                </button>
              </div>
              <div className="flex" role="list" aria-label="Miniaturas">
                {images.slice(arrayMinIndex(), arrayMaxIndex()).map((image, index) => {
                  return (
                    <div key={index} className="w-1/5 cursor-pointer img-hover-zoom" role="listitem">
                      <img
                        onClick={() => setSlideIndex(index)}
                        src={getImagePath(image)}
                        className={"image-thumbnail object-cover h-24 hover:opacity-100 cursor-pointer " + (slideIndex === index ? "opacity-100" : "opacity-50")}
                        alt={`Miniatura trabajo ${arrayMinIndex() + index + 1}`}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export default OurWork;
